import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Check, ArrowRight } from "lucide-react";
import { InlineEditableItem, AddInlineItem } from "@/components/InlineEditable";

/**
 * CategoriesCarousel — horizontal multi-select carousel with 3D rotation on hover.
 * - Click a card: toggle selection (checked state with fluo-green overlay)
 * - CTA at the bottom: "Mostra selezionate (N) →" navigates to /catalogo?categories=a,b,c
 * - Supports inline admin editing (edit title / change image / delete / add)
 *
 * Props:
 *   categories: Array<{ key, label, img }>
 *   updateCategory(i, field, value), deleteCategory(i), addCategory()
 */
export default function CategoriesCarousel({ categories, updateCategory, deleteCategory, addCategory }) {
  const scrollerRef = useRef(null);
  const nav = useNavigate();
  const [selected, setSelected] = useState(new Set());
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const toggleSelect = (key, e) => {
    // Prevent navigation on card click; selection is the primary action.
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const amount = Math.max(320, Math.floor(el.clientWidth * 0.7));
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  };

  const updateScrollState = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 8);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  };

  useEffect(() => {
    updateScrollState();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [categories]);

  const goToCatalog = () => {
    if (selected.size === 0) {
      nav("/catalogo");
      return;
    }
    const list = [...selected].join(",");
    nav(`/catalogo?categories=${encodeURIComponent(list)}`);
  };

  return (
    <div data-testid="categories-carousel" className="relative">
      {/* Arrows */}
      <button
        data-testid="cat-prev"
        aria-label="Scorri indietro"
        onClick={() => scrollBy(-1)}
        disabled={!canScrollLeft}
        className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 items-center justify-center bg-white border border-black/15 shadow-xl hover:bg-black hover:text-white hover:border-black disabled:opacity-30 disabled:cursor-not-allowed transition-all -translate-x-1/2"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        data-testid="cat-next"
        aria-label="Scorri avanti"
        onClick={() => scrollBy(1)}
        disabled={!canScrollRight}
        className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 items-center justify-center bg-white border border-black/15 shadow-xl hover:bg-black hover:text-white hover:border-black disabled:opacity-30 disabled:cursor-not-allowed transition-all translate-x-1/2"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      {/* Scrollable row (gives 3D depth to children) */}
      <div
        ref={scrollerRef}
        className="flex gap-6 overflow-x-auto overflow-y-visible pb-6 pt-10 px-2 snap-x snap-mandatory scroll-smooth no-scrollbar"
        style={{ perspective: "1400px" }}
      >
        {categories.map((c, i) => {
          const isSelected = selected.has(c.key);
          return (
            <div
              key={c.key + i}
              className="shrink-0 w-[260px] md:w-[320px] lg:w-[360px] snap-start"
              style={{ perspective: "1200px" }}
            >
              <InlineEditableItem
                title={c.label}
                image={c.img}
                label="categoria"
                onEditTitle={(v) => updateCategory(i, "label", v)}
                onEditImage={(v) => updateCategory(i, "img", v)}
                onDelete={() => deleteCategory(i)}
              >
                <button
                  type="button"
                  data-testid={`category-${c.key}`}
                  aria-pressed={isSelected}
                  onClick={(e) => toggleSelect(c.key, e)}
                  className={`cat-card group relative aspect-video w-full overflow-hidden border transition-all block will-change-transform ${isSelected ? "border-[#22ff66] shadow-[0_30px_60px_-20px_rgba(34,255,102,0.55)]" : "border-white/10 hover:border-[#22ff66]"}`}
                  style={{ transformStyle: "preserve-3d", background: "#0a0a0a" }}
                >
                  <img
                    src={c.img}
                    alt={c.label}
                    className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-110"
                    style={{ transform: "translateZ(0)" }}
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/70 to-transparent p-5 pt-16 pointer-events-none">
                    <h3
                      className="heading-uc text-xl md:text-2xl mb-1 text-white"
                      style={{ textShadow: "0 4px 20px rgba(0,0,0,0.85), 0 2px 6px rgba(0,0,0,0.7)" }}
                    >
                      {c.label}
                    </h3>
                    <span
                      className="text-xs uppercase tracking-widest text-[#22ff66] flex items-center gap-2 font-bold"
                      style={{ textShadow: "0 2px 8px rgba(0,0,0,0.8)" }}
                    >
                      {isSelected ? "Selezionata" : "Seleziona"}
                    </span>
                  </div>
                  {/* Selected tick */}
                  {isSelected && (
                    <div
                      className="absolute top-3 right-3 w-10 h-10 rounded-full bg-[#22ff66] text-black flex items-center justify-center shadow-[0_0_20px_rgba(34,255,102,0.6)] pointer-events-none"
                      style={{ transform: "translateZ(40px)" }}
                    >
                      <Check className="w-5 h-5" strokeWidth={3} />
                    </div>
                  )}
                  {/* Shine on hover */}
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                    style={{
                      background: "linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 40%)",
                      mixBlendMode: "overlay",
                    }}
                  />
                </button>
              </InlineEditableItem>
            </div>
          );
        })}

        {/* Add new category inline */}
        <div className="shrink-0 w-[260px] md:w-[320px] lg:w-[360px] snap-start">
          <AddInlineItem testid="add-category" label="Aggiungi" onClick={addCategory} aspect="aspect-video" />
        </div>
      </div>

      {/* CTA bar */}
      <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
        <div className="text-xs uppercase tracking-widest text-white/60">
          {selected.size === 0 ? (
            <>Clicca una o più categorie per filtrare</>
          ) : (
            <>
              <span className="text-[#22ff66] font-black">{selected.size}</span> categori{selected.size === 1 ? "a" : "e"} selezionat{selected.size === 1 ? "a" : "e"}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <button
              data-testid="cat-clear"
              onClick={() => setSelected(new Set())}
              className="h-11 px-4 border border-white/15 hover:bg-white/5 text-xs uppercase tracking-widest font-semibold transition"
            >
              Pulisci
            </button>
          )}
          <button
            data-testid="cat-apply"
            onClick={goToCatalog}
            className="h-11 px-6 bg-[#22ff66] hover:bg-[#4EFF87] text-black uppercase tracking-widest text-xs font-black flex items-center gap-2 shadow-[0_15px_40px_-15px_rgba(34,255,102,0.7)] transition"
          >
            {selected.size > 0 ? `Mostra selezionate (${selected.size})` : "Vedi tutte"} <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Hover rotation + hide scrollbar */}
      <style>{`
        .cat-card {
          transition: transform 450ms cubic-bezier(0.2, 0.9, 0.2, 1), box-shadow 450ms ease, border-color 300ms ease;
          transform: perspective(1200px) rotateY(0deg) rotateX(0deg) translateY(0) scale(1);
        }
        .cat-card:hover {
          transform: perspective(1200px) rotateY(-9deg) rotateX(6deg) translateY(-8px) scale(1.03);
          box-shadow: 0 40px 60px -24px rgba(0,0,0,0.55), 0 0 30px -8px rgba(34,255,102,0.35);
        }
        @media (prefers-reduced-motion: reduce) {
          .cat-card, .cat-card:hover { transform: none !important; transition: border-color 300ms ease; }
        }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { scrollbar-width: none; -ms-overflow-style: none; }
      `}</style>
    </div>
  );
}
